import {useState, useMemo} from 'react'
import {RepoPushIcon, TriangleDownIcon} from '@primer/octicons-react'
import {SelectPanel, Button, type SelectPanelItemInput, Truncate} from '@primer/react'
import type {Ruleset} from '../../types/rules-types'

import styles from './RulesetFilter.module.css'

export function RulesetFilter({
  currentRuleset,
  allRulesets,
  onSelect,
}: {
  currentRuleset: Ruleset | undefined
  allRulesets: Ruleset[]
  onSelect: (ruleset: Ruleset | undefined) => void
}) {
  const [filter, setFilter] = useState('')

  type SelectPanelItenInputWithFilter = SelectPanelItemInput & {filterText: string}

  const rulesetItems: SelectPanelItenInputWithFilter[] = useMemo(() => {
    // Create items for the SelectPanel based on the all rulesets
    return allRulesets.map(ruleset => ({
      id: ruleset.id,
      filterText: ruleset.name, // Use text for filtering
      // We are using children in place of the text prop
      children: (
        <Truncate maxWidth="100%" title={ruleset.name}>
          {ruleset.name}
        </Truncate>
      ),
    }))
  }, [allRulesets])

  const filteredRulesets = rulesetItems.filter((ruleset: SelectPanelItenInputWithFilter) =>
    ruleset.filterText.toLowerCase().includes(filter.toLowerCase()),
  )

  const selectedRuleset: SelectPanelItemInput | undefined = useMemo(() => {
    if (!currentRuleset) {
      return undefined
    }
    return rulesetItems.find(r => r.id === currentRuleset.id)
  }, [currentRuleset, rulesetItems])

  const [open, setOpen] = useState(false)

  const viewAllRulesets = () => {
    onSelect(undefined)
    setFilter('')
    setOpen(false)
  }

  return (
    <SelectPanel
      title="Select a ruleset"
      placeholderText="Find a ruleset..."
      renderAnchor={anchorProps => (
        <Button
          {...anchorProps}
          leadingVisual={RepoPushIcon}
          trailingAction={TriangleDownIcon}
          className={styles.RulesetFilterButtonText}
        >
          <Truncate title={currentRuleset?.name ? currentRuleset.name : 'All rulesets'}>
            {currentRuleset?.name ? currentRuleset.name : 'All rulesets'}
          </Truncate>
        </Button>
      )}
      open={open}
      onOpenChange={setOpen}
      items={filteredRulesets}
      selected={selectedRuleset}
      onSelectedChange={(selected: SelectPanelItemInput | undefined) => {
        for (const ruleset of allRulesets) {
          if (ruleset.id === selected?.id) {
            return onSelect(ruleset)
          }
        }
        onSelect(undefined)
      }}
      onFilterChange={setFilter}
      overlayProps={{maxHeight: 'medium', width: 'medium'}}
      message={
        filteredRulesets.length === 0
          ? {
              variant: 'empty',
              title: 'Nothing to show',
              body: '',
            }
          : undefined
      }
      secondaryAction={
        <SelectPanel.SecondaryActionLink onClick={viewAllRulesets}>
          View for all rulesets
        </SelectPanel.SecondaryActionLink>
      }
    />
  )
}
